import { Link } from "react-router";

import { ErrorState } from "../../components/ErrorState";
import { ListSkeleton } from "../../components/Loading";
import type { AdminStats as Stats, DayCount } from "../../lib/api";
import { formatRelative, plural } from "../../lib/format";
import { useAdminStats } from "../../lib/queries";
import { useTitle } from "../../lib/useTitle";

const number = new Intl.NumberFormat("fr-FR");
const dayLabel = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });

export function AdminStats() {
  useTitle("Administration");
  const stats = useAdminStats();
  if (stats.isError) return <ErrorState error={stats.error} onRetry={() => stats.refetch()} />;
  if (!stats.data) return <ListSkeleton rows={4} />;
  const s = stats.data;

  return (
    <div className="admin-stats">
      {s.users.pending_approval > 0 && (
        <div className="notice notice--warning" role="status">
          <span>
            {plural(s.users.pending_approval, "compte attend", "comptes attendent")} votre validation.{" "}
            <Link to="/admin/utilisateurs?statut=pending">Les examiner</Link>
          </span>
        </div>
      )}

      <RegistrationSummary s={s} />

      <section aria-labelledby="stats-users">
        <h2 id="stats-users">Utilisateurs</h2>
        <dl className="kpis">
          <Kpi label="Comptes" value={s.users.total} note={`+${number.format(s.users.new_7d)} en 7 jours`} />
          <Kpi label="Adresses confirmées" value={s.users.confirmed} />
          <Kpi label="Non confirmées" value={s.users.unconfirmed} />
          <Kpi label="En attente de validation" value={s.users.pending_approval} />
          <Kpi label="Administrateurs" value={s.users.admins} />
        </dl>
        <DailyBars title="Inscriptions par jour, 30 derniers jours" unit="inscription" days={s.signups} />
      </section>

      <section aria-labelledby="stats-activity">
        <h2 id="stats-activity">Activité</h2>
        <dl className="kpis">
          <Kpi label="Surveillances actives" value={s.watches.active} note={`${number.format(s.watches.stopped)} arrêtées`} />
          <Kpi
            label="Entreprises suivies"
            value={s.watches.companies_watched}
            note={`${number.format(s.watches.companies)} connues`}
          />
          <Kpi label="Événements, 30 jours" value={s.events.last_30d} note={`${number.format(s.events.total)} au total`} />
          <Kpi
            label="Alertes envoyées, 30 jours"
            value={s.notifications.sent_30d}
            note={`${number.format(s.notifications.skipped_30d)} non envoyées (préférences)`}
          />
          <Kpi label="Imports en cours" value={s.imports.running} note={`${number.format(s.imports.total)} au total`} />
        </dl>
        <DailyBars title="Nouveaux événements détectés par jour, 30 derniers jours" unit="événement" days={s.alerts} />
      </section>

      <div className="admin-columns">
        <section aria-labelledby="stats-kinds">
          <h2 id="stats-kinds">Types d'événements, 30 jours</h2>
          <KindBars kinds={s.events_by_kind} />
        </section>

        <section aria-labelledby="stats-sources">
          <h2 id="stats-sources">Sources</h2>
          {s.sources.length ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">Source</th>
                    <th scope="col" className="num">
                      Entreprises
                    </th>
                    <th scope="col" className="num">
                      En échec
                    </th>
                    <th scope="col">Dernier succès</th>
                  </tr>
                </thead>
                <tbody>
                  {s.sources.map((src) => (
                    <tr key={src.source}>
                      <th scope="row">
                        <Link to={`/admin/donnees/source_states?where=${encodeURIComponent(JSON.stringify({ source: src.source }))}`}>
                          {src.source}
                        </Link>
                      </th>
                      <td className="num">{number.format(src.companies)}</td>
                      <td className={`num${src.failing ? " is-negative" : ""}`}>{number.format(src.failing)}</td>
                      <td>{formatRelative(src.last_success_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty">Aucune source interrogée pour l'instant.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function RegistrationSummary({ s }: { s: Stats }) {
  const { registrations_open: open, approval_required: approval } = s.registration;
  return (
    <p className="admin-registration">
      Inscriptions <strong>{open ? "ouvertes" : "fermées"}</strong>
      {open && <>, {approval ? "validation manuelle des nouveaux comptes" : "sans validation"}</>}.{" "}
      <Link to="/admin/utilisateurs">Modifier</Link>
    </p>
  );
}

function Kpi({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="kpi">
      <dt>{label}</dt>
      <dd>
        <span className="kpi__value">{number.format(value)}</span>
        {note && <span className="kpi__note">{note}</span>}
      </dd>
    </div>
  );
}

const CHART = { width: 600, height: 150, base: 130, top: 12 };

/** One bar per day. SVG attributes only: the CSP forbids inline styles. */
function DailyBars({ title, unit, days }: { title: string; unit: string; days: DayCount[] }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  const total = days.reduce((sum, d) => sum + d.count, 0);
  const slot = CHART.width / days.length;
  const barWidth = Math.max(2, slot - 4);
  const label = (d: DayCount) => `${dayLabel.format(new Date(`${d.day}T00:00:00`))} : ${plural(d.count, unit, `${unit}s`)}`;

  return (
    <figure className="daily-chart">
      <figcaption>
        {title} <span className="daily-chart__total">{plural(total, unit, `${unit}s`)}</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        role="img"
        aria-label={`${title} : ${plural(total, unit, `${unit}s`)}, au plus ${max} par jour.`}
      >
        <line className="daily-chart__grid" x1="0" x2={CHART.width} y1={CHART.top} y2={CHART.top} />
        <text className="daily-chart__axis" x="0" y={CHART.top - 3}>
          {max}
        </text>
        <line className="daily-chart__baseline" x1="0" x2={CHART.width} y1={CHART.base} y2={CHART.base} />
        {days.map((d, i) => {
          const h = (d.count / max) * (CHART.base - CHART.top);
          const x = i * slot + (slot - barWidth) / 2;
          return (
            <g key={d.day} className="daily-chart__day">
              <title>{label(d)}</title>
              {/* Hit area taller and wider than the bar. */}
              <rect className="daily-chart__hit" x={i * slot} y="0" width={slot} height={CHART.base} />
              {d.count > 0 && <path className="daily-chart__bar" d={barPath(x, CHART.base, barWidth, h)} />}
            </g>
          );
        })}
        {[0, Math.floor(days.length / 2), days.length - 1].map((i) => (
          <text
            key={i}
            className="daily-chart__axis"
            x={i === 0 ? 0 : i === days.length - 1 ? CHART.width : i * slot + slot / 2}
            y={CHART.height - 2}
            textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"}
          >
            {dayLabel.format(new Date(`${days[i]?.day}T00:00:00`))}
          </text>
        ))}
      </svg>
    </figure>
  );
}

/** Bar with rounded top corners, square on the baseline. */
function barPath(x: number, base: number, width: number, height: number) {
  const r = Math.min(3, width / 2, height);
  const top = base - height;
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + width - r}Q${x + width},${top} ${x + width},${top + r}V${base}Z`;
}

function KindBars({ kinds }: { kinds: Stats["events_by_kind"] }) {
  if (!kinds.length) return <p className="empty">Aucun nouvel événement sur la période.</p>;
  const max = Math.max(...kinds.map((k) => k.count));
  return (
    <ul className="kind-bars">
      {kinds.map((k) => (
        <li key={k.kind} className={`kind-bars__row kind-bars__row--${k.severity}`}>
          <span className="kind-bars__label">{k.label}</span>
          <svg className="kind-bars__track" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
            <rect className="kind-bars__bar" x="0" y="0" height="10" width={Math.max(1, (k.count / max) * 100)} />
          </svg>
          <span className="kind-bars__count">{number.format(k.count)}</span>
        </li>
      ))}
    </ul>
  );
}
